import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { viabilidadeService, usersService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { maskCEP } from '../utils/masks';
import '../styles/ConsultaViabilidade.css';

const ConsultaViabilidade = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [operadorasHabilitadas, setOperadorasHabilitadas] = useState(null);
  const [cep, setCep] = useState('');
  const [numero, setNumero] = useState('');
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);
  const [operadoraSelecionada, setOperadoraSelecionada] = useState(null);
  const [apiOffline, setApiOffline] = useState(false);

  const temRestricaoOperadoras = operadorasHabilitadas !== null && operadorasHabilitadas.length > 0;

  useEffect(() => {
    const carregarOperadorasHabilitadas = async () => {
      if (!user?.id || user.role !== 'vendedor') {
        setOperadorasHabilitadas(null);
        return;
      }
      try {
        const data = await usersService.getOperadoras(user.id);
        if (data.operadoras?.length > 0) {
          setOperadorasHabilitadas(data.operadoras);
        } else {
          setOperadorasHabilitadas(null);
        }
      } catch (err) {
        console.error('Erro ao carregar operadoras habilitadas:', err);
        setOperadorasHabilitadas(null);
      }
    };
    carregarOperadorasHabilitadas();
  }, [user?.id, user?.role]);

  const handleCepChange = (e) => {
    const value = maskCEP(e.target.value);
    setCep(value);
    setError('');
  };

  const handleNumeroChange = (e) => {
    setNumero(e.target.value);
    setError('');
  };

  const handleConsultar = async (e) => {
    e.preventDefault();

    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) {
      setError('CEP deve ter 8 dígitos');
      return;
    }

    if (!numero.trim()) {
      setError('Número é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResultado(null);
      setOperadoraSelecionada(null);
      setApiOffline(false);

      const data = await viabilidadeService.consultar(cepLimpo, numero.trim());
      
      if (data.success) {
        setResultado(data);
        // Verificar se a API está offline
        setApiOffline(data.api_offline || false);
      } else {
        setError(data.error || 'Erro ao consultar viabilidade');
        setApiOffline(false);
      }
    } catch (err) {
      console.error('Erro ao consultar viabilidade:', err);
      
      // Se for erro 401 (não autorizado), o token pode estar expirado
      if (err.response?.status === 401) {
        setError('Sua sessão expirou ou você não está autenticado. Por favor, faça login novamente.');
        // Limpar token e redirecionar após 2 segundos
        setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }, 2000);
      } else {
        setError(err.response?.data?.error || 'Erro ao consultar viabilidade. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarOperadora = (nomeOperadora, coverage) => {
    setOperadoraSelecionada({ nome: nomeOperadora, coverage });
  };

  const handleContinuar = () => {
    if (!operadoraSelecionada) {
      alert('Por favor, selecione uma operadora');
      return;
    }

    // Navegar para NovaVenda com os dados
    navigate('/vendas/nova', {
      state: {
        cep: cep.replace(/\D/g, ''),
        numero: numero.trim(),
        operadora_selecionada: operadoraSelecionada.nome,
        operadora_viabilidade_json: resultado
      }
    });
  };

  // Processar operadoras quando resultado mudar
  const operadoras = useMemo(() => {
    if (!resultado || !resultado.operadoras) return [];
    
    // Converter objeto de operadoras em array
    const operadorasArray = Object.keys(resultado.operadoras).map(nome => {
      const opData = resultado.operadoras[nome];
      return {
        nome,
        ...opData,
        // Normalizar coverage para boolean
        coverage: opData.coverage === true || opData.coverage === 'true' || opData.coverage === 1
      };
    });

    return operadorasArray;
  }, [resultado]);

  // Filtrar operadoras: coverage normalizado para boolean
  const operadorasComViabilidade = operadoras.filter(op => op.coverage === true);
  const operadorasSemViabilidade = operadoras.filter(op => op.coverage === false);

  return (
    <Layout>
      <div className="consulta-viabilidade-page">
        <div className="page-header">
          <h1>🔍 Consulta de Viabilidade</h1>
          <p>Informe o CEP e número para verificar a disponibilidade de operadoras</p>
        </div>

        {temRestricaoOperadoras && (
          <div className="info-message" style={{
            background: 'rgba(13, 110, 253, 0.15)',
            border: '1px solid var(--azul-dvs, #0d6efd)',
            color: 'var(--texto-primario)',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            Você está habilitado apenas para:{' '}
            <strong>
              {operadorasHabilitadas
                .map((op) => op.nome_display || op.nome.charAt(0).toUpperCase() + op.nome.slice(1))
                .join(', ')}
            </strong>
          </div>
        )}

        <form onSubmit={handleConsultar} className="consulta-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cep">CEP *</label>
              <input
                type="text"
                id="cep"
                value={cep}
                onChange={handleCepChange}
                placeholder="00000-000"
                maxLength={9}
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="numero">Número *</label>
              <input
                type="text"
                id="numero"
                value={numero}
                onChange={handleNumeroChange}
                placeholder="123"
                disabled={loading}
                required
              />
            </div>
          </div>

          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Consultando...' : '🔍 Consultar Viabilidade'}
          </button>
        </form>

        {resultado && (
          <div className="resultado-container">
            <h2>Operadoras Disponíveis</h2>
            
            {apiOffline && resultado.warning && (
              <div className="warning-message" style={{
                background: 'rgba(255, 193, 7, 0.2)',
                border: '1px solid #ffc107',
                color: '#ffc107',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                ⚠️ <strong>Atenção:</strong> {resultado.warning}
              </div>
            )}
            
            <p style={{ color: 'var(--texto-secundario)', marginBottom: '1rem' }}>
              Total encontrado: {operadoras.length} operadora{operadoras.length !== 1 ? 's' : ''} 
              ({operadorasComViabilidade.length} com viabilidade, {operadorasSemViabilidade.length} sem viabilidade confirmada)
            </p>

            {operadorasComViabilidade.length > 0 && (
              <div className="operadoras-section">
                <h3>✅ Operadoras com Viabilidade</h3>
                <div className="operadoras-grid">
                  {operadorasComViabilidade.map((operadora) => (
                    <div
                      key={operadora.nome}
                      className={`operadora-card ${operadoraSelecionada?.nome === operadora.nome ? 'selected' : ''}`}
                      onClick={() => handleSelecionarOperadora(operadora.nome, true)}
                    >
                      <div className="operadora-name">
                        {operadora.nome.charAt(0).toUpperCase() + operadora.nome.slice(1)}
                      </div>
                      <div className="operadora-status">
                        <span className="badge-success">✓ Viabilidade Confirmada</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {operadorasSemViabilidade.length > 0 && (
              <div className="operadoras-section">
                <h3>⚠️ Operadoras sem Viabilidade Confirmada</h3>
                <p className="warning-text">
                  Estas operadoras podem ser selecionadas, mas o BackOffice consultará manualmente. 
                  Há alta probabilidade de não haver viabilidade.
                </p>
                <div className="operadoras-grid">
                  {operadorasSemViabilidade.map((operadora) => (
                    <div
                      key={operadora.nome}
                      className={`operadora-card warning ${operadoraSelecionada?.nome === operadora.nome ? 'selected' : ''}`}
                      onClick={() => {
                        const confirmar = window.confirm(
                          `Atenção: ${operadora.nome} não possui viabilidade confirmada. O BackOffice consultará manualmente.\n\nDeseja continuar?`
                        );
                        if (confirmar) {
                          handleSelecionarOperadora(operadora.nome, false);
                        }
                      }}
                    >
                      <div className="operadora-name">
                        {operadora.nome.charAt(0).toUpperCase() + operadora.nome.slice(1)}
                      </div>
                      <div className="operadora-status">
                        <span className="badge-warning">⚠ Sem Viabilidade Confirmada</span>
                      </div>
                      {operadora.message && (
                        <div className="operadora-message">{operadora.message}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {operadoras.length === 0 && (
              <div className="no-operadoras">
                <p>
                  {temRestricaoOperadoras
                    ? 'Nenhuma das suas operadoras habilitadas retornou resultado para este endereço.'
                    : 'Nenhuma operadora encontrada para este endereço.'}
                </p>
              </div>
            )}

            {operadoraSelecionada && (
              <div className="operadora-selecionada">
                <h3>Operadora Selecionada:</h3>
                <div className="selected-operadora">
                  <strong>{operadoraSelecionada.nome.charAt(0).toUpperCase() + operadoraSelecionada.nome.slice(1)}</strong>
                  {operadoraSelecionada.coverage ? (
                    <span className="badge-success">✓ Viabilidade Confirmada</span>
                  ) : (
                    <span className="badge-warning">⚠ Sem Viabilidade Confirmada</span>
                  )}
                </div>
                <button onClick={handleContinuar} className="btn-primary">
                  ➡️ Continuar para Dados da Venda
                </button>
              </div>
            )}
          </div>
        )}

        <div className="actions">
          <button onClick={() => navigate('/dashboard')} className="btn-secondary">
            🏠 Voltar ao Dashboard
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default ConsultaViabilidade;
